import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface OverflowTab {
  id: string;
  label: string;
}

interface UseTabOverflowResult<T extends OverflowTab> {
  /** Ref callback for the visible tab container; drives width measurement. */
  setContainerRef: (node: HTMLDivElement | null) => void;
  /** Ref callback for a hidden strip rendering every tab at natural width. */
  setMeasurementRef: (node: HTMLDivElement | null) => void;
  visibleTabs: T[];
  overflowTabs: T[];
}

/**
 * Splits a tab list into visible + overflow buckets based on the actual
 * rendered width of each label. The active tab is always kept visible.
 *
 * Consumers must render a hidden strip containing every `availableTab` and
 * attach `setMeasurementRef`. Tabs are located via `[role="tab"]`.
 */
export function useTabOverflow<T extends OverflowTab>(
  availableTabs: T[],
  activeTabId: string,
  overflowMenuReservedWidth = 40,
): UseTabOverflowResult<T> {
  const [containerWidth, setContainerWidth] = useState(0);
  const [tabWidths, setTabWidths] = useState<number[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measurementNodeRef = useRef<HTMLDivElement | null>(null);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) {
      setContainerWidth(0);
      return;
    }
    setContainerWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const setMeasurementRef = useCallback((node: HTMLDivElement | null) => {
    measurementNodeRef.current = node;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  useEffect(() => {
    const node = measurementNodeRef.current;
    if (!node) return;
    const widths = Array.from(node.querySelectorAll<HTMLElement>('[role="tab"]')).map((el) =>
      Math.ceil(el.getBoundingClientRect().width),
    );
    setTabWidths((prev) =>
      prev.length === widths.length && prev.every((w, i) => w === widths[i]) ? prev : widths,
    );
  }, [availableTabs]);

  return useMemo<UseTabOverflowResult<T>>(() => {
    const passthrough = {
      setContainerRef,
      setMeasurementRef,
      visibleTabs: availableTabs,
      overflowTabs: [] as T[],
    };

    // Until we've measured every tab, assume they all fit.
    if (availableTabs.length === 0 || containerWidth <= 0 || tabWidths.length !== availableTabs.length) {
      return passthrough;
    }

    const totalWidth = tabWidths.reduce((sum, w) => sum + w, 0);
    if (totalWidth <= containerWidth) {
      return passthrough;
    }

    const available = containerWidth - overflowMenuReservedWidth;
    let used = 0;
    let fitCount = 0;
    for (const w of tabWidths) {
      if (used + w > available) break;
      used += w;
      fitCount++;
    }
    const visibleCount = Math.max(1, fitCount);

    // Reserve a visible slot for the active tab when it doesn't fit naturally.
    const activeIndex = availableTabs.findIndex((t) => t.id === activeTabId);
    const activeInOverflow = activeIndex >= visibleCount;
    const headCount = visibleCount - (activeInOverflow ? 1 : 0);

    const visible: T[] = [];
    const overflow: T[] = [];
    availableTabs.forEach((tab, i) => {
      if (i < headCount || (activeInOverflow && tab.id === activeTabId)) {
        visible.push(tab);
      } else {
        overflow.push(tab);
      }
    });

    return { setContainerRef, setMeasurementRef, visibleTabs: visible, overflowTabs: overflow };
  }, [
    availableTabs,
    containerWidth,
    tabWidths,
    activeTabId,
    overflowMenuReservedWidth,
    setContainerRef,
    setMeasurementRef,
  ]);
}
